import { useTranslation } from 'react-i18next';
import type { UseQueryResult } from '@tanstack/react-query';
import type { PlatformMetricsSummary } from '@/api/platformMetrics/platformMetricsApi.types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { useAuth } from '@/hooks/useAuth';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { usePlatformMetricsSummaryQuery } from '@/hooks/usePlatformMetricsSummaryQuery';
import { MetricTile } from './MetricTile';
import { RecentWorkspacesPanel } from './RecentWorkspacesPanel';
import { RecentActivityPanel } from './RecentActivityPanel';
import { DashboardChartsSection } from './DashboardChartsSection';

export function AdminDashboard() {
  const { t } = useTranslation('admin');
  const { currentUser } = useAuth();
  const canReadMetrics = useHasPlatformPermission('platform_metrics.read');
  const metricsQuery = usePlatformMetricsSummaryQuery(canReadMetrics);
  const firstName = currentUser === null ? '' : currentUser.fullName.trim().split(/\s+/)[0];

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zooindigo">
          {t('dashboard.greeting', { name: firstName })}
        </h1>
        <p className="text-zoomuted text-sm mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {canReadMetrics === true ? <MetricsRow metricsQuery={metricsQuery} /> : null}

      {canReadMetrics === true ? (
        <div className="mt-6">
          <DashboardChartsSection isEnabled={canReadMetrics} />
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
        <RecentWorkspacesPanel />
        <RecentActivityPanel />
      </div>
    </div>
  );
}

function MetricsRow({
  metricsQuery,
}: {
  metricsQuery: UseQueryResult<PlatformMetricsSummary, Error>;
}) {
  const { t } = useTranslation('admin');

  if (metricsQuery.isLoading === true) {
    return (
      <div className="py-10 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }
  if (metricsQuery.isError === true || metricsQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          metricsQuery.refetch();
        }}
      />
    );
  }

  const summary = metricsQuery.data;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
      <MetricTile
        iconName="Building"
        label={t('dashboard.metric.workspaces')}
        value={String(summary.totalWorkspaces)}
        subLabel={t('dashboard.metric.workspacesActiveSuspended', {
          active: summary.activeWorkspaces,
          suspended: summary.suspendedWorkspaces,
        })}
      />
      <MetricTile
        iconName="UsersRound"
        label={t('dashboard.metric.accounts')}
        value={String(summary.totalAccounts)}
        subLabel={t('dashboard.metric.accountsActive', { active: summary.activeAccounts })}
      />
      <MetricTile
        iconName="Activity"
        label={t('dashboard.metric.activity')}
        value={String(summary.recentActivityCount)}
        subLabel={t('dashboard.metric.activityWindow', { days: summary.recentActivityDays })}
      />
    </div>
  );
}
